import { type DocumentNode, gql, useLazyQuery } from '@apollo/client';
import type { User } from '@interfaces/user';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, type SxProps } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { Chip as OVChip } from '@ovComponents/2-component/chip/chip';
import { UserContext } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { Box, Typography } from 'hephaestus/web';
import { pick } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_SEARCH_RESULTS = 12;
export type Item = Required<{
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  entityName: string;
  primaryEmail?: string;
}>;
export type SearchResults = { items: Item[]; count: number };
export type SearchFn = (query: string) => Promise<SearchResults>;

export const SEARCH_CLIENT = gql`
  query searchUsers($query: String!, $organizationId: ObjectID!, $affiliateUsersOnly: Boolean, $skippedUserIds: [ObjectID!]) {
    searchUsers(input: {
      filter: { query: $query, organizationId: $organizationId, affiliateUsersOnly: $affiliateUsersOnly, skippedUserIds: $skippedUserIds }
      pagination: { perPage: ${MAX_SEARCH_RESULTS} },
    }) {
      totalCount
      users {
        id
        firstName middleName lastName entityName
        dateOfBirth
        primaryEmail
        affiliateOnly
        sinExists
        type
      }
    }
  }
`;

const matchingFn = (item: Item, value: string) =>
  [
    item.id,
    item.firstName,
    item.middleName,
    item.lastName,
    item.entityName,
    item.primaryEmail,
    // This is required for when serach query extends across multiple name fragments
    generateClientNameString(item),
  ].some((str) => str?.toLowerCase()?.includes(value.toLowerCase()));

export const customEntitySearchFnFactory =
  (entities: User[]): SearchFn =>
  (query: string) =>
    new Promise<SearchResults>((resolve) => {
      const items = entities.map(
        (x) =>
          pick(x, [
            'id',
            'firstName',
            'middlename',
            'lastName',
            'entityName',
            'primaryEmail',
          ]) as Item,
      );
      const matches = items.filter((item) => matchingFn(item, query));
      resolve({
        items: matches,
        count: matches.length,
      });
    });

export const ClientSelectField = ({
  user,
  setUser,
  label,
  disabled,
  fullWidth,
  isJointOrAuthorizedIndividual,
  labelSx,
  width,
  error,
  errorText,
  customSearchFn,
  onBlur,
  autoFocus,
  customSearchQuery,
  affiliateLabel,
  affiliationUsers,
  setDisableEditForm,
  skippedUserIds,
}: {
  user: Partial<User> | undefined;
  setUser: (user: User | undefined) => void;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
  isJointOrAuthorizedIndividual?: boolean;
  labelSx?: SxProps;
  width?: string;
  error?: boolean;
  errorText?: string;
  customSearchFn?: SearchFn;
  onBlur?: (e: React.ChangeEvent) => void;
  autoFocus?: boolean;
  customSearchQuery?: DocumentNode;
  affiliateLabel?: boolean;
  affiliationUsers?: any[];
  setDisableEditForm?: (disableEditForm: boolean) => void;
  skippedUserIds?: string[] | undefined;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation('components');
  const [searchResult, setSearchResult] = useState<SearchResults>({
    items: [],
    count: 0,
  });
  const [searchUsers, { loading }] = useLazyQuery(
    customSearchQuery ?? SEARCH_CLIENT,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [accessibleUserIds, setAccessibleUserIds] = useState<string[]>([]);
  const defaultSearchFn = (query: string): Promise<SearchResults> =>
    new Promise<SearchResults>((resolve) => {
      if (disabled) return;
      const userIdsToSkip = customSearchQuery ? undefined : skippedUserIds;
      searchUsers({
        variables: {
          query,
          organizationId: activeOrganization.id,
          affiliateUsersOnly: isJointOrAuthorizedIndividual ? false : undefined,
          skippedUserIds: userIdsToSkip,
        },
        onCompleted: (data) => {
          let totalCount = Number(data.searchUsers.totalCount);
          const users = data.searchUsers.users || [];
          setAccessibleUserIds(
            users.map((accessibleUser: Item) => accessibleUser.id),
          );
          const searchedUsers = [
            ...users,
            ...(affiliationUsers
              ? []
              : [
                  {
                    firstName: '',
                    lastName: '',
                    entityName: '',
                    id: '',
                  },
                ]),
          ];
          if (affiliationUsers) {
            // Only add affiliation users that don't already exist in searchedUsers
            const uniqueAffiliationUsers = affiliationUsers.filter(
              (affUser) =>
                !searchedUsers.some(
                  (existingUser) => existingUser.id === affUser.id,
                ),
            );
            totalCount += uniqueAffiliationUsers.length;
            searchedUsers.push(...uniqueAffiliationUsers);
          }
          resolve({
            items: searchedUsers,
            count: totalCount,
          });
        },
        onError: () => {
          resolve({
            items: [],
            count: 0,
          });
        },
      }).then();
    });

  const searchFn = customSearchFn ?? defaultSearchFn;

  // Initial query to prevent empty dropdown
  const initSearchResult = async () => {
    setSearchResult(await searchFn(''));
  };

  useEffect(() => {
    initSearchResult();
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
      initSearchResult();
    }
  }, [disabled, autoFocus]);

  return (
    <Autocomplete
      width={width}
      data-testid='userSelectField'
      freeSolo
      loading={loading}
      disabled={disabled}
      options={searchResult.items}
      fullWidth={fullWidth}
      label={label ?? ''}
      value={user}
      getOptionLabel={(option) => generateClientNameString(option)}
      renderOption={(props: any, option: any) => {
        const isAffiliate = affiliateLabel && option.affiliateOnly;
        return (
          <li {...props}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography variant='bodyLarge'>
                {generateClientNameString(option)}
              </Typography>
              {isAffiliate && (
                <OVChip
                  text={t('clientSelect.affiliate')}
                  small
                  sx={{ m: 0 }}
                />
              )}
            </Box>
          </li>
        );
      }}
      isOptionEqualToValue={(option: Item, value: Item) =>
        option.id === value.id
      }
      onInputChange={async (_e: React.SyntheticEvent, value: string) =>
        setSearchResult(await searchFn(value))
      }
      onChange={(_e: React.SyntheticEvent, value: Item, reason: string) => {
        if (reason === 'clear') {
          setUser(undefined);
          setDisableEditForm?.(false);
        } else if (value.id) {
          const result = searchResult.items.find(
            (item) => item.id === value.id,
          );
          setUser(result as User);
          if (result && !accessibleUserIds?.includes(value.id)) {
            setDisableEditForm?.(true);
          } else {
            setDisableEditForm?.(false);
          }
        }
      }}
      onBlur={onBlur}
      filterOptions={(options: Item[], state: any) =>
        options.filter((item) => matchingFn(item, state?.inputValue))
      }
      renderTags={(tagValue: any, getTagProps: any) =>
        tagValue.map((option: any, index: number) => (
          <Chip
            label={option}
            sx={{ marginTop: '-2px !important' }}
            {...getTagProps({ index })}
            size='small'
            onDelete={undefined}
          />
        ))
      }
      componentsProps={{ popupIndicator: { sx: { display: 'none' } } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        inputRef,
        disabled,
      }}
      labelSx={labelSx}
      error={error}
      errorText={errorText}
      autoFocus={autoFocus}
    />
  );
};
